
import { defineComponent, nextTick, PropType, ref, watch } from 'vue'
import { useReflectiveInjector } from '@tanbo/vue-di-plugin'
import { GroupEditService } from '@/views/dashboard/_components/used-group/group-edit.service'
import { Team } from '@/services/team'
import { Group } from '@/views/dashboard/_type/group'
import { PolicyType } from '@/views/dashboard/_enum/policy-type'
import { flattenArray } from '@/utils/tool'
import { IS_PRIVATE } from '@/common/const/env'
import { IS_JP_ONLY_BUILD } from '@/common/const/build-locale'

export default defineComponent({
  emits: ['update', 'update:modelValue', 'update:list'],

  props: {
    modelValue: {
      type: Array as PropType<number[]>,
      default: []
    },
    maxHeight: {
      type: Number,
      default: 180
    },
    width: {
      type: Number,
      default: 400
    },
    disabled: {
      type: Boolean,
      default: false
    },
    showTip: {
      type: Boolean,
      default: true
    },
    strategyOver: {
      type: Object as PropType<{ type?: PolicyType; current?: string }>,
      default: (): { type?: PolicyType; current?: string } => ({}),
    },
    permissionKey: {
      type: String,
      default: ''
    },
    /** 预设GroupId（子级也会一起勾选） */
    presetGroupId: {
      type: Number
    },
    /** 在分组树右上角展示说明 Popover（由 PolicyBindScope 传入说明插槽） */
    showGroupTipPopover: {
      type: Boolean,
      default: false
    }
  },

  setup(props, context) {
    watch(() => props.modelValue, async (nv) => {
      await nextTick()
      treeNode.value?.setCheckedKeys(nv ?? [])
      context.emit('update:list', getLabelList(nv))
      // 禁止在此 emit update:modelValue：父级若传入 computed 映射后的新数组引用，
      // 回传会再次触发本 watch，造成死循环。勾选变化由 handleCheck / checkPresetGroupId 负责 emit。
      handleScroll()
    }, { immediate: true })
    const injector = useReflectiveInjector([GroupEditService])
    const service = injector.get(GroupEditService)
    const team = injector.get(Team)

    const treeNode = ref({} as any)
    const scrollerRef = ref({} as any)
    const groupData = ref({} as Group)
    const scrollSelectedSwitch = ref(true)
    const currentTeamId = ref<number>(undefined)

    team.currentTeam.subscribe(info => {
      currentTeamId.value = info.team_id
    })
    function updateData() {
      service.getTerminalGroup({
        team_id: currentTeamId.value,
        system_types: IS_PRIVATE ? [1,3] : [],
        strategy_type: props.strategyOver.type,
        permission_name: props.permissionKey
      }).then(response => {
        groupData.value = response.group
        checkPresetGroupId()
        handleScroll()
      })
    }
    function getLabelList(keys: any[]) {
      const flatArray = flattenArray([groupData.value], [])
      const idSet = new Set((keys || []).map((k) => +k).filter((k) => !Number.isNaN(k)))
      return flatArray.filter((item) => idSet.has(+item.group_id))
    }
    function isKeyChecked(checkedKeys: any[], gid: number) {
      return (checkedKeys || []).some((k) => +k === +gid)
    }
    async function handleCheck(node: Group, data) {
      scrollSelectedSwitch.value = false
      checkChildNode(node, isKeyChecked(data.checkedKeys, node.group_id))
      const keys = treeNode.value?.getCheckedKeys()
      treeNode.value?.setCheckedKeys(keys)
      context.emit('update:modelValue', keys)
      context.emit('update:list', getLabelList(keys))
      context.emit('update', keys)
    }

    function checkPresetGroupId () {
      if (props.presetGroupId === undefined) return
      const keys = service.getGroupAndChildrenIds(groupData.value, +props.presetGroupId)
      treeNode.value?.setCheckedKeys(keys)
      context.emit('update:modelValue', keys)
      context.emit('update:list', getLabelList(keys))
      context.emit('update', keys)
    }

    function checkChildNode (node: Group, isCheck: boolean) {
      const deep = (children: Group[] | undefined) => {
        (children ?? []).forEach((v) => {
          treeNode.value?.setChecked(v, isCheck)
          deep(v.children)
        })
      }
      deep(node?.children)
    }

    async function scrollToSelected () {
      const container = scrollerRef.value as HTMLElement
      const selectedNode = container.querySelector('.el-tree-node.is-checked .el-checkbox.is-checked') as HTMLElement
      if (!container || !selectedNode) return

      const containerRect = container.getBoundingClientRect()
      const demoNodeRect = selectedNode.getBoundingClientRect()
      const relativeTop = demoNodeRect.top - containerRect.top + container.scrollTop
      // 计算需要滚动到的位置（使元素居中显示）
      const targetScrollTop = relativeTop - container.clientHeight / 2 + selectedNode.offsetHeight / 2
      container.scrollTop = Math.max(0, Math.min(targetScrollTop, container.scrollHeight - container.clientHeight))
    }

    function handleScroll () {
      if (!scrollSelectedSwitch.value) {
        return
      }

      nextTick(() => scrollToSelected())
    }

    updateData()

    return {
      IS_JP_ONLY_BUILD,
      treeNode,
      scrollerRef,
      groupData,
      handleCheck,
    }
  }
})
